import { getLanguage } from '@/shared/i18n'
import { createTraceId } from '@/shared/lib'
import { ApiError, NetworkError } from './errors'
import { fetchTransport } from './fetch-transport'
import { getInitData } from './init-data'
import type { Transport, TransportResponse } from './transport'
import type { ApiFailureBody, ApiResult, ApiSuccessBody, HttpMethod, QueryParams } from './types'

let transportPromise: Promise<Transport> | null = null

/**
 * Транспорт выбирается по `.env` на этапе сборки: условие статическое, и в
 * сборке без моков фикстуры не попадают в бандл вовсе.
 */
const getTransport = (): Promise<Transport> => {
  transportPromise ??=
    import.meta.env.VITE_USE_MOCK === 'true'
      ? import('./mock').then((module) => module.mockTransport)
      : Promise.resolve(fetchTransport)

  return transportPromise
}

const buildUrl = (path: string, query?: QueryParams): string => {
  if (!query) {
    return path
  }

  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value))
    }
  }

  const search = params.toString()

  return search ? `${path}?${search}` : path
}

const isEnvelope = (json: unknown): json is ApiSuccessBody<unknown> | ApiFailureBody =>
  typeof json === 'object' && json !== null && 'success' in json

const isAbort = (error: unknown): boolean => error instanceof DOMException && error.name === 'AbortError'

export type RequestOptions = {
  query?: QueryParams
  body?: unknown
  signal?: AbortSignal
}

export const request = async <T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<ApiResult<T>> => {
  const traceId = createTraceId()
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // Сообщения об ошибках и тексты витрины локализует бэкенд.
    'Accept-Language': getLanguage(),
    'X-Request-Id': traceId,
  }
  const initData = getInitData()

  if (initData) {
    headers.Authorization = `tma ${initData}`
  }

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  let response: TransportResponse

  try {
    const transport = await getTransport()

    response = await transport({
      method,
      url: buildUrl(path, options.query),
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    })
  } catch (error) {
    // Отмена запроса — не ошибка: TanStack Query распознаёт её сам.
    if (isAbort(error)) {
      throw error
    }

    throw new NetworkError(traceId, error)
  }

  const { status, json } = response
  // На экране ошибки — идентификатор, под которым запрос лёг в логи бэкенда,
  // а не тот, что мы предложили (TMA-Б-9). Без заголовка — свой.
  const responseTraceId = response.requestId ?? traceId

  if (isEnvelope(json)) {
    if (json.success) {
      return { data: json.data as T, meta: json.meta }
    }

    throw new ApiError({ status, code: json.code, message: json.message, data: json.data, traceId: responseTraceId })
  }

  throw new ApiError({
    status,
    code: `HTTP-${status}`,
    message: `Unexpected response ${status}`,
    traceId: responseTraceId,
  })
}

type NoBody = Omit<RequestOptions, 'body'>

/** HTTP-клиент. Компоненты его не вызывают — только api-сегменты сущностей. */
export const http = {
  get: <T>(path: string, options?: NoBody) => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: NoBody) => request<T>('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: NoBody) => request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: NoBody) => request<T>('DELETE', path, options),
}
